import clsx from 'clsx';
import {
  useEffect,
  useId,
  useMemo,
  type ReactNode,
  TouchEvent,
  Children,
} from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import { isElement } from 'react-is';

import {
  _canReceiveDefaultTextFormat,
  type DataTestId,
  type DOMProps,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { Flex, Grid } from '@dynatrace/strato-components/layouts';
import { Highlight } from '@dynatrace/strato-components/typography';

import * as styles from './Suggestion.sty.js';
import { SuggestionDetails } from './SuggestionDetails.js';
import { Chip } from '../../../content/chip/Chip.js';
import { useFilterFieldContext } from '../hooks/useFilterFieldContext.js';
import { FilterFieldActions } from '../state/filter-field-state-reducer.js';
import type {
  FilterFieldPrimitive,
  FilterFieldValuePredicate,
} from '../types/validation.js';

/**
 * Helper type for defining the properties of a FilterField suggestion.
 * @public
 */
export type FilterFieldSuggestion = {
  /**
   * The value used when applying the suggestion.
   * @defaultValue undefined
   */
  value?: string;
  /**
   * The display value to show for the suggestion.
   */
  displayValue?: ReactNode;
  /**
   * Additional information to render for the suggestion.
   */
  details?: ReactNode;
  /**
   * The display value to show for the suggestion.
   */
  icon?: ReactNode;
  /**
   * The characters to add to the suggestion when applying.
   * @defaultValue space
   */
  trailing?: string;
  /**
   * Insertion mode used when applying the suggestion.
   * @defaultValue 'replace-token'
   */
  insertionStrategy?:
    | 'replace-token'
    | 'replace-statement'
    | 'replace-all'
    | 'insert';
  /**
   * Whether the cursor should be set before the trailing characters.
   * @defaultValue false
   */
  cursorBeforeTrailing?: boolean;
  /**
   * Defines the type of the suggestion and provides a type indication by default.
   * When used with the `ValidatorMap` it may be derived from their predicates.
   */
  valueType?: FilterFieldPrimitive;
};

/**
 * Accepted properties for FilterFieldSuggestion.
 * @public
 */
export type FilterFieldSuggestionProps = WithChildren &
  StylingProps &
  DataTestId &
  DOMProps &
  FilterFieldSuggestion;

type FilterFieldSuggestionSlots = {
  label: ReactNode[];
  details: ReactNode;
};

const supportedValuesMessage = defineMessage({
  defaultMessage: 'Supported values: {supportedValues}',
  id: 'jLuGsPmQuWW6Nt7s',
  description:
    'Message shown for value suggestions restricted by a validator map',
});

/** @internal */
function getDescription(type: FilterFieldValuePredicate) {
  let supportedValues = '';

  switch (type) {
    case 'Boolean':
      supportedValues = "'true', 'false'";
      break;
    case 'Duration':
      supportedValues = 'Durations';
      break;
    case 'Number':
      supportedValues = 'Numbers';
      break;
    case 'String':
    case 'Any':
    default:
      return undefined;
  }

  return (
    <FormattedMessage
      {...supportedValuesMessage}
      values={{ supportedValues }}
    />
  );
}

/**
 * @public
 */
export const Suggestion = (props: FilterFieldSuggestionProps) => {
  const {
    value,
    displayValue,
    details,
    trailing = ' ',
    cursorBeforeTrailing = false,
    insertionStrategy = 'replace-token',
    valueType,
    className: consumerClassName,
    style: consumerStyle,
    icon,
    'data-testid': dataTestId,
    children,
    id,
  }: FilterFieldSuggestionProps = props;
  const { applySuggestion, dispatch, term, focusedSuggestion } =
    useFilterFieldContext();
  const generatedId = useId();
  const uniqueId = id ?? generatedId;

  const slots = useMemo(() => {
    const slots: FilterFieldSuggestionSlots = {
      label: [],
      details: null,
    };

    function getSlots(children: ReactNode, slots: FilterFieldSuggestionSlots) {
      Children.forEach(children, (child) => {
        if (isElement(child)) {
          switch (child.type) {
            case SuggestionDetails:
              slots.details = child;
              break;
            default:
              slots.label.push(child);
          }
        } else {
          slots.label.push(child);
        }
      });
    }
    getSlots(children, slots);

    return slots;
  }, [children]);

  const childIsText = _canReceiveDefaultTextFormat(slots.label);
  const displayValueIsText = _canReceiveDefaultTextFormat(displayValue);
  const isFocused = uniqueId === focusedSuggestion ? true : undefined;

  const finalDetails = useMemo(() => {
    if (slots.details) {
      return slots.details;
    }

    if (details) {
      return <SuggestionDetails>{details}</SuggestionDetails>;
    }

    if (valueType) {
      return <SuggestionDetails>{getDescription(valueType)}</SuggestionDetails>;
    }

    return null;
  }, [details, slots.details, valueType]);

  if (value === undefined && !childIsText && !displayValueIsText) {
    // eslint-disable-next-line no-console
    console.warn(
      'Suggestion value: There is no value provided for a suggestion and the children are not text-only. The applied result may be unexpected.',
    );
  }

  let suggestionText = '';
  if (value) {
    suggestionText = value;
  } else if (childIsText) {
    suggestionText = slots.label.join('');
  } else if (displayValueIsText) {
    suggestionText = displayValue as string;
  }

  if (typeof suggestionText === 'string') {
    suggestionText = suggestionText.trim();
  }

  useEffect(() => {
    if (typeof suggestionText === 'string') {
      dispatch({
        type: FilterFieldActions.ADDSUGGESTION,
        payload: {
          id: uniqueId,
          applySuggestion: () => {
            applySuggestion(
              suggestionText,
              trailing,
              insertionStrategy,
              cursorBeforeTrailing,
            );
          },
        },
      });
    }

    return () =>
      dispatch({
        type: FilterFieldActions.REMOVESUGGESTION,
        payload: { id: uniqueId },
      });
    // Only run on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (typeof suggestionText !== 'string') {
    return null;
  }

  if (!value && trailing === '') {
    return (
      <Flex
        as="li"
        key={value}
        role="option"
        justifyContent="space-between"
        className={clsx(styles.suggestion, styles.suggestionsEmptyState)}
        aria-selected="false"
      >
        <span>
          {slots.label.length > 0 ? slots.label : displayValue ?? value}
        </span>
        {finalDetails}
      </Flex>
    );
  }

  return (
    <Grid
      as="li"
      data-testid={dataTestId}
      id={uniqueId}
      data-strato-filter-field-suggestion-id={uniqueId}
      role="option"
      aria-selected={isFocused}
      className={clsx(
        styles.suggestion,
        icon ? styles.suggestionWithIcon : '',
        { 'virtual-focus': isFocused },
        consumerClassName,
      )}
      // NOTE: There's an open issue in @testing-library/user-event where `onClick` is not triggered in tests when used
      // with `event.preventDefault`. Pointer down also works and enables easier testing of the suggestions.
      onPointerDown={() => {
        applySuggestion(
          suggestionText,
          trailing,
          insertionStrategy,
          cursorBeforeTrailing,
        );
      }}
      onTouchEnd={(e: TouchEvent) => {
        // Defaults for on-touch-end events needs to be prevented, as otherwise on touch devices the suggestion window
        // gets closed when clicking on suggestions. Note: This can be emulated via dev tools but only happens
        // on deployed apps (not happening in local dev mode)
        e.preventDefault();
      }}
      style={consumerStyle}
    >
      <Flex minWidth={0}>
        {icon && (
          <Chip size="condensed">
            <Chip.Prefix>{icon}</Chip.Prefix>
          </Chip>
        )}
        {/* The highlight splits the text into a mark and a span, need to wrap it to keep it together in the flex container */}
        {id !== 'strato-filterfield-suggestion-use-as-value' ? (
          <span className={styles.suggestionText}>
            <Highlight term={term ?? ''}>
              {slots.label.length > 0 ? slots.label : displayValue ?? value}
            </Highlight>
          </span>
        ) : (
          <span>
            {slots.label.length > 0 ? slots.label : displayValue ?? value}
          </span>
        )}
      </Flex>
      {finalDetails}
    </Grid>
  );
};

Suggestion.displayValue = 'FilterField.Suggestion';
